import { Component, input, model, output, signal, computed } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { DividerModule } from "primeng/divider";
import { Lobby } from "../../../../Utils/_models/lobbies.model";

export interface ILobbyAttendee {
  userId: any;
  joinedAt?: Date | string;
  balloonStatus?: string;
  roundEliminated?: number;
  role: 'Participant' | 'Interest' | 'Spectator';
}

@Component({
  selector: "app-lobby-details-modal",
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TagModule,
    DividerModule,
    DatePipe
  ],
  templateUrl: "./lobby-details-modal.component.html",
  styleUrl: "./lobby-details-modal.component.css",
})
export class LobbyDetailsModalComponent {
  lobby = input<Lobby | null>(null);
  visible = model<boolean>(false);
  onClose = output();

  hostPreviewUrl = signal<string | null>(null);

  selectedTab = signal<'all' | 'participants' | 'interests' | 'spectators'>('all');

  allAttendees = computed<ILobbyAttendee[]>(() => {
    const l = this.lobby();
    if (!l) return [];
    
    const parts = (l.participantsList || []).map(p => ({ ...p, role: 'Participant' as const }));
    const ints = (l.interestsList || []).map(p => ({ ...p, role: 'Interest' as const }));
    const specs = (l.spectatorsList || []).map(p => ({ ...p, role: 'Spectator' as const }));
    
    return [...parts, ...ints, ...specs];
  });

  filteredAttendees = computed<ILobbyAttendee[]>(() => {
    const all = this.allAttendees();
    const tab = this.selectedTab();
    if (tab === 'all') return all;
    if (tab === 'participants') return all.filter(a => a.role === 'Participant');
    if (tab === 'interests') return all.filter(a => a.role === 'Interest');
    if (tab === 'spectators') return all.filter(a => a.role === 'Spectator');
    return all;
  });

  get attendeesCount() {
    return {
      all: this.allAttendees().length,
      participants: this.allAttendees().filter(a => a.role === 'Participant').length,
      interests: this.allAttendees().filter(a => a.role === 'Interest').length,
      spectators: this.allAttendees().filter(a => a.role === 'Spectator').length
    };
  }

  setTab(tab: 'all' | 'participants' | 'interests' | 'spectators') {
    this.selectedTab.set(tab);
  }

  getStatusSeverity(status: string): string {
    const statusMap: Record<string, string> = {
      live: "p-tag-success",
      upcoming: "p-tag-info",
      completed: "p-tag-primary",
      cancelled: "p-tag-danger",
    };
    return statusMap[(status || "").toLowerCase()] || "p-tag-secondary";
  }

  openImageInNewTab(event: MouseEvent): void {
    event.stopPropagation();
    const url = this.lobby()?.hostPhotoUrl;
    if (url) {
      window.open(url, "_blank");
    }
  }

  closeHostPreview(): void {
    this.hostPreviewUrl.set(null);
  }

  closeDialog(): void {
    this.selectedTab.set('all');
    this.onClose.emit();
  }
}
